// src/pages/MemberDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import postTicket, { SkippedDuplicate } from '../utils/postTicket'; // [marker: fixes-oct21 v1]
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { CreateTaskModal } from './TasksPage'; // [marker: invoice-links v1]
import WebsiteListing from '../components/WebsiteListing'; // [marker: website-feed v1]

export default function MemberDetailPage() {
  const { id } = useParams();
  const { isManagerOrAbove, isSuperAdmin } = useAuth();
  const [member, setMember] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [financials, setFinancials] = useState(null);
  const [linkedTasks, setLinkedTasks] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showEditMember, setShowEditMember] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [addingTask, setAddingTask] = useState(false); // [marker: invoice-links v1]
  const [showAddSponsorship, setShowAddSponsorship] = useState(false);
  const [showAddTicket, setShowAddTicket] = useState(false);

  const loadMember = () => {
    api.get(`/businesses/${id}`)
      .then(res => setMember(res.data))
      .catch(() => toast.error('Failed to load member.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMember(); }, [id]);

  useEffect(() => {
    if (activeTab === 'financial' && !financials) {
      api.get(`/businesses/${id}/financial-history`)
        .then(res => setFinancials(res.data))
        .catch(console.error);
    }
    if (activeTab === 'notes') {
      loadNotes();
    }
    if (activeTab === 'tasks' && linkedTasks === null) {
      loadLinkedTasks();
    }
  }, [activeTab, id]);

  const loadLinkedTasks = async () => {
    try {
      const res = await api.get(`/tasks?linked_entity_type=business&linked_entity_id=${id}&view=all&show_snoozed=true&limit=100`);
      setLinkedTasks(res.data.data || []);
    } catch (err) {
      console.error('Failed to load linked tasks:', err);
      setLinkedTasks([]);
    }
  };

  const loadNotes = async () => {
    const contacts = member?.contacts || [];
    if (contacts.length === 0) {
      setNotes([]);
      return;
    }
    try {
      const results = await Promise.all(
        contacts.map(c => api.get(`/contacts/${c.id}/notes`).then(r => r.data).catch(() => []))
      );
      const all = results.flat().sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return db - da;
      });
      setNotes(all);
    } catch (err) {
      console.error('Failed to load notes:', err);
      setNotes([]);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    if (!member?.contacts?.length) {
      toast.error('Please add a contact first — notes are attached to contacts.');
      return;
    }
    try {
      await api.post(`/contacts/${member.contacts[0].id}/notes`, { noteText: newNote });
      setNewNote('');
      toast.success('Note added.');
      loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add note.');
    }
  };

  const handleRenew = async () => {
    if (!window.confirm('Mark this membership as renewed?')) return;
    try {
      await api.post(`/membership/businesses/${id}/renew`);
      toast.success('Membership renewed.');
      loadMember();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to renew.');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(
      `Archive "${member.business_name}"?\n\n` +
      `The business and all its contacts, invoices, and history will be preserved but hidden from default views. ` +
      `You can restore it later if needed.`
    )) return;
    try {
      await api.delete(`/businesses/${id}`);
      toast.success('Business archived.');
      window.location.href = '/members';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to archive.');
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm(`Restore "${member.business_name}" to active status?`)) return;
    try {
      await api.post(`/businesses/${id}/unarchive`);
      toast.success('Business restored.');
      loadMember();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to restore.');
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.first_name} ${contact.last_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      toast.success('Contact deleted.');
      loadMember();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete contact.');
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await api.delete(`/notes/${note.id}`);
      toast.success('Note deleted.');
      loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete note.');
    }
  };

  if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;
  if (!member) return <div className="empty-state"><h3>Member not found</h3><Link to="/members">← Back to Members</Link></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 4 }}>
            <Link to="/members" style={{ color: 'var(--gray-400)' }}>Members</Link> / {member.business_name}
          </div>
          <h1 className="page-title">{member.business_name}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <span className={`badge badge-${member.status}`}>{member.status?.replace('_',' ')}</span>
            {member.membership_type_name && <span className="text-muted">{member.membership_type_name}</span>}
            {member.salesperson_initials && <span className="text-muted">· {member.salesperson_initials}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isManagerOrAbove() && (member.status === 'grace_period' || member.status === 'lapsed') && (
            <button className="btn btn-primary" onClick={handleRenew}>Renew Membership</button>
          )}
          {isManagerOrAbove() && member.status !== 'archived' && (
            <button className="btn btn-secondary" onClick={() => setShowEditMember(true)}>Edit</button>
          )}
          <Link to={`/invoices?business_id=${id}`} className="btn btn-secondary">View Invoices</Link>
          {isSuperAdmin() && member.status === 'archived' && (
            <button className="btn btn-secondary" onClick={handleUnarchive}>Restore</button>
          )}
          {isSuperAdmin() && member.status !== 'archived' && (
            <button className="btn btn-danger" onClick={handleArchive} title="Archive this business (preserves all data)">Archive</button>
          )}
        </div>
      </div>

      <div className="tabs">
        {['profile','contacts','notes','financial','tasks'].map(tab => (
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'contacts' && <span style={{ marginLeft: 4, fontSize: '0.75rem', color: 'var(--gray-400)' }}>({member.contacts?.length || 0})</span>}
            {tab === 'tasks' && linkedTasks && <span style={{ marginLeft: 4, fontSize: '0.75rem', color: 'var(--gray-400)' }}>({linkedTasks.length})</span>}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Business Information</span></div>
            <div className="card-body">
              <InfoRow label="Business Name" value={member.business_name} />
              <InfoRow label="Email"         value={member.email} />
              <InfoRow label="Phone"         value={member.phone} />
              <InfoRow label="Website"       value={member.website} link />
              <InfoRow label="Address"       value={[member.address_line1, member.city && `${member.city}, ${member.state} ${member.zip}`].filter(Boolean).join(', ')} />
              <InfoRow label="Industry"      value={member.industry_name} />
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Membership Details</span></div>
            <div className="card-body">
              <InfoRow label="Membership Type" value={member.membership_type_name} />
              <InfoRow label="Annual Dues"     value={member.annual_dues ? `$${parseFloat(member.annual_dues).toLocaleString()}` : '—'} />
              <InfoRow label="Status"          value={<span className={`badge badge-${member.status}`}>{member.status?.replace('_',' ')}</span>} />
              <InfoRow label="Member Since"    value={fmtDate(member.member_since)} />
              <InfoRow label="Renewal Month"   value={member.renewal_month} />
              <InfoRow label="Next Renewal"    value={fmtDate(member.next_renewal_date)} />
              <InfoRow label="Salesperson"     value={member.salesperson_name} />
            </div>
          </div>
          <WebsiteListing member={member} canEdit={isManagerOrAbove()} onChanged={loadMember} /> {/* [marker: website-feed v1] */}
          {member.notes && (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header"><span className="card-title">Business Notes</span></div>
              <div className="card-body">
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{member.notes}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div>
          {isManagerOrAbove() && (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddContact(true)}>+ Add Contact</button>
            </div>
          )}
          <div className="card">
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>Name</th><th>Title</th><th>Email</th><th>Phone</th><th>Type</th><th>Portal Role</th><th style={{ width: 50 }}></th></tr></thead>
                <tbody>
                  {!member.contacts?.length ? (
                    <tr><td colSpan={7}>
                      <div className="empty-state" style={{ padding: 32 }}>
                        <div className="empty-state-icon">👤</div>
                        <h3>No contacts yet</h3>
                        {isManagerOrAbove() && <p>Click "+ Add Contact" to add the first contact for this business.</p>}
                      </div>
                    </td></tr>
                  ) : member.contacts.map(c => (
                    <tr key={c.id}>
                      <td className="td-primary">
                        <Link to={`/contacts/${c.id}`} style={{ color: 'inherit' }}>{c.salutation ? `${c.salutation} ` : ''}{c.first_name} {c.last_name}</Link>
                      </td>
                      <td>{c.title || '—'}</td>
                      <td>{c.email || '—'}</td>
                      <td>{c.phone || '—'}</td>
                      <td><span className="badge badge-navy" style={{ fontSize: '0.65rem' }}>{c.contact_type?.replace(/_/g,' ')}</span></td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{c.portal_role?.replace('_',' ') || '—'}</td>
                      <td>
                        {isManagerOrAbove() && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-icon" onClick={() => setEditingContact(c)} title="Edit contact">✎</button>
                            <button className="btn btn-ghost btn-icon" onClick={() => handleDeleteContact(c)} title="Delete contact" style={{ color: 'var(--danger)' }}>✕</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div>
          {isManagerOrAbove() && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <label className="form-label">Add Note</label>
                {!member.contacts?.length && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--warning)', marginBottom: 8 }}>
                    ⚠ Notes require a contact. Please add a contact on the Contacts tab first.
                  </p>
                )}
                <textarea
                  className="form-textarea"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  placeholder="Enter note..."
                  rows={3}
                  disabled={!member.contacts?.length}
                />
                <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={addNote} disabled={!member.contacts?.length}>
                  Add Note
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {notes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <h3>No notes yet</h3>
                {!member.contacts?.length && <p>Add a contact first to enable notes.</p>}
              </div>
            ) : notes.map(note => (
              <div key={note.id} className="card">
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{note.staff_first_name} {note.staff_last_name}</span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginLeft: 8 }}>{fmtDateTime(note.created_at)}</span>
                    </div>
                    {isManagerOrAbove() && (
                      <button className="btn btn-ghost btn-icon" onClick={() => handleDeleteNote(note)} title="Delete note" style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>✕</button>
                    )}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{note.note_text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Financial History Tab */}
      {activeTab === 'financial' && (
        <div>
          {isManagerOrAbove() && (
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {/* [marker: invoice-links v1] full invoice screen (dates, Item List, income accounts) pre-filled for this company */}
              <Link className="btn btn-primary btn-sm" to={`/invoices/new?businessId=${id}${member.contacts?.[0]?.id ? `&contactId=${member.contacts[0].id}` : ''}`}>+ Add Invoice</Link>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddSponsorship(true)}>+ Add Sponsorship</button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddTicket(true)}>+ Add Ticket</button>
            </div>
          )}
          {!financials ? <div className="loading-page"><div className="spinner" /></div> : (
            <>
              <div className="stat-grid" style={{ marginBottom: 20 }}>
                <div className="stat-card"><div className="stat-label">Total Invoiced</div><div className="stat-value">${parseFloat(financials.summary?.total_invoiced || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="stat-label">Total Paid</div><div className="stat-value" style={{ color: 'var(--success)' }}>${parseFloat(financials.summary?.total_paid || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="stat-label">Outstanding</div><div className="stat-value" style={{ color: parseFloat(financials.summary?.total_outstanding) > 0 ? 'var(--danger)' : 'var(--success)' }}>${parseFloat(financials.summary?.total_outstanding || 0).toLocaleString()}</div></div>
              </div>
              {/* [marker: invoice-links v1] sponsorships for this company, wherever they were created */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><span className="card-title">Sponsorships</span></div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr><th>Event</th><th>Level</th><th>Amount</th><th>Status</th><th>Invoice</th></tr></thead>
                    <tbody>
                      {!financials.sponsorships?.length ? (
                        <tr><td colSpan={5}><div className="empty-state" style={{ padding: 20 }}><p>No sponsorships</p></div></td></tr>
                      ) : financials.sponsorships.map(sp => (
                        <tr key={sp.id}>
                          <td>{sp.event_id ? <Link to={`/events/${sp.event_id}`} className="td-primary">{sp.event_name}</Link> : '—'}
                            {sp.event_date && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{fmtDate(sp.event_date + 'T00:00:00')}</div>}</td>
                          <td>{sp.tier_name || '—'}</td>
                          <td>${parseFloat(sp.amount || 0).toLocaleString()}</td>
                          <td><span className={`badge badge-${sp.payment_status}`}>{String(sp.payment_status || '').replace('_', ' ')}</span></td>
                          <td>{sp.invoice_id ? <Link to={`/invoices/${sp.invoice_id}`} className="td-mono">{sp.invoice_number}</Link> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><span className="card-title">Invoices</span></div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr><th>Invoice #</th><th>For</th><th>Amount</th><th>Paid</th><th>Status</th><th>Date</th></tr></thead>
                    <tbody>
                      {!financials.invoices?.length ? (
                        <tr><td colSpan={6}><div className="empty-state" style={{ padding: 20 }}><p>No invoices</p></div></td></tr>
                      ) : financials.invoices.map(inv => (
                        <tr key={inv.id}>
                          <td><Link to={`/invoices/${inv.id}`} className="td-primary td-mono">{inv.invoice_number}</Link></td>
                          <td style={{ fontSize: '0.85rem', maxWidth: 280 }}>{inv.items || <span style={{ textTransform: 'capitalize' }}>{inv.invoice_type}</span>}</td>
                          <td>${parseFloat(inv.total_amount).toLocaleString()}</td>
                          <td>${parseFloat(inv.amount_paid).toLocaleString()}</td>
                          <td><span className={`badge badge-${inv.status}`}>{inv.status?.replace('_',' ')}</span></td>
                          <td style={{ fontSize: '0.8rem' }}>{fmtDate(inv.issue_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><span className="card-title">Payments</span></div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th>Amount</th></tr></thead>
                    <tbody>
                      {!financials.payments?.length ? (
                        <tr><td colSpan={4}><div className="empty-state" style={{ padding: 20 }}><p>No payments</p></div></td></tr>
                      ) : financials.payments.map(p => {
                        const m = p.notes && p.notes.match(/QB Pay Meth: ([^·]+)/);
                        const method = m ? m[1].trim() : (p.payment_method || '').replace('_', ' ');
                        return (
                          <tr key={p.id}>
                            <td style={{ fontSize: '0.8rem' }}>{fmtDate(p.paid_at)}</td>
                            <td style={{ textTransform: 'capitalize' }}>{method}</td>
                            <td className="td-mono" style={{ fontSize: '0.8rem' }}>{p.reference_number || p.check_number || '—'}</td>
                            <td>${parseFloat(p.amount).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">Tickets Purchased</span></div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr><th>Ticket #</th><th>Event</th><th>Buyer</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>
                      {!financials.tickets?.length ? (
                        <tr><td colSpan={5}><div className="empty-state" style={{ padding: 20 }}><p>No tickets</p></div></td></tr>
                      ) : financials.tickets.map(t => (
                        <tr key={t.id}>
                          <td className="td-mono" style={{ fontSize: '0.78rem' }}>{t.ticket_number}</td>
                          <td>{t.event_name} <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>{fmtDate(t.event_date)}</span></td>
                          <td>{t.buyer_first_name} {t.buyer_last_name}</td>
                          <td>${parseFloat(t.amount_paid).toLocaleString()}</td>
                          <td><span className={`badge badge-${t.payment_status}`}>{t.payment_status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tasks Tab (NEW - Phase 4b) */}
      {activeTab === 'tasks' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setAddingTask(true)}>+ Add Task</button>
          </div>
          {linkedTasks === null ? (
            <div className="loading-page"><div className="spinner" /></div>
          ) : (
            <LinkedTasksList tasks={linkedTasks} />
          )}
          {addingTask && (
            <CreateTaskModal
              initialLink={{ type: 'business', id: member.id, name: member.business_name }}
              onClose={() => setAddingTask(false)}
              onSaved={() => { setAddingTask(false); loadLinkedTasks(); }}
            />
          )}
        </div>
      )}

      {showAddContact && (
        <AddContactModal
          businessId={id}
          onClose={() => setShowAddContact(false)}
          onSaved={() => { setShowAddContact(false); loadMember(); }}
        />
      )}

      {editingContact && (
        <EditContactModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSaved={() => { setEditingContact(null); loadMember(); }}
        />
      )}

      {showEditMember && (
        <EditMemberModal
          member={member}
          onClose={() => setShowEditMember(false)}
          onSaved={() => { setShowEditMember(false); loadMember(); }}
        />
      )}

      {showAddInvoice && (
        <AddInvoiceModal
          businessId={id}
          primaryContact={member.contacts?.[0]}
          onClose={() => setShowAddInvoice(false)}
          onSaved={() => { setShowAddInvoice(false); setFinancials(null); }}
        />
      )}

      {showAddSponsorship && (
        <AddSponsorshipModal
          businessId={id}
          businessName={member.business_name}
          primaryContact={member.contacts?.[0]}
          onClose={() => setShowAddSponsorship(false)}
          onSaved={() => { setShowAddSponsorship(false); setFinancials(null); }}
        />
      )}

      {showAddTicket && (
        <AddTicketFromMemberModal
          businessId={id}
          businessName={member.business_name}
          primaryContact={member.contacts?.[0]}
          onClose={() => setShowAddTicket(false)}
          onSaved={() => { setShowAddTicket(false); setFinancials(null); }}
        />
      )}
    </div>
  );
}

// ─── LinkedTasksList (Phase 4b) ──────────────────────────────────────────────
function LinkedTasksList({ tasks }) {
  const PRIORITY_STYLES = {
    urgent: { color: '#dc2626', bg: '#fef2f2' },
    high:   { color: '#d97706', bg: '#fffbeb' },
    normal: { color: '#1a3a5c', bg: '#f0f4f8' },
    low:    { color: '#9ca3af', bg: '#f9fafb' },
  };

  if (!tasks?.length) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">✅</div>
        <h3>No tasks linked to this member</h3>
        <p>Click “+ Add Task” to add one for this business.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="table-wrap" style={{ border: 'none' }}>
        <table>
          <thead>
            <tr><th>Title</th><th>Priority</th><th>Status</th><th>Due</th><th>Assigned To</th></tr>
          </thead>
          <tbody>
            {tasks.map(t => {
              const isSnoozed = t.snoozed_until && new Date(t.snoozed_until) > new Date();
              const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'complete';
              const ps = PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal;
              return (
                <tr key={t.id} style={{ opacity: t.status === 'complete' ? 0.5 : (isSnoozed ? 0.6 : 1) }}>
                  <td>
                    <Link to={`/tasks`} state={{ taskId: t.id }} style={{ fontWeight: 600 }}>
                      {t.title}
                    </Link>
                    {isSnoozed && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#7c3aed' }}>💤</span>}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.7rem', background: ps.bg, color: ps.color, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                      {t.priority}
                    </span>
                  </td>
                  <td><span className="badge" style={{ fontSize: '0.7rem' }}>{t.status?.replace('_',' ')}</span></td>
                  <td style={{ color: isOverdue ? 'var(--danger)' : undefined, fontSize: '0.85rem' }}>
                    {t.due_date ? fmtDate(t.due_date) : '—'}
                  </td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                    {t.assigned_to_first_name ? `${t.assigned_to_first_name} ${t.assigned_to_last_name}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddContactModal({ businessId, onClose, onSaved }) {
  const [form, setForm] = useState({ salutation: '', firstName: '', lastName: '', title: '', email: '', phone: '', contactType: 'general_staff', portalRole: 'member_contact' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSave = async () => {
    if (!form.firstName || !form.lastName) return toast.error('First and last name required.');
    setSaving(true);
    try {
      await api.post('/contacts', { ...form, businessId });
      toast.success('Contact added.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add Contact</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Salutation</label>
              <input className="form-input" value={form.salutation} onChange={e => set('salutation', e.target.value)} placeholder="Mr., Ms., Dr., etc." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="CEO, President, etc." />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Type</label>
              <select className="form-select" value={form.contactType} onChange={e => set('contactType', e.target.value)}>
                <option value="primary_contact">Primary Contact</option>
                <option value="decision_maker">Decision Maker</option>
                <option value="billing_contact">Billing Contact</option>
                <option value="event_coordinator">Event Coordinator</option>
                <option value="general_staff">General Staff</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Portal Access</label>
            <select className="form-select" value={form.portalRole} onChange={e => set('portalRole', e.target.value)}>
              <option value="member_admin">Member Admin (full access)</option>
              <option value="member_contact">Member Contact (read only)</option>
              <option value="">No portal access</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Add Contact'}</button>
        </div>
      </div>
    </div>
  );
}

function EditContactModal({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    salutation:   contact.salutation || '',
    firstName:    contact.first_name || '',
    lastName:     contact.last_name || '',
    title:        contact.title || '',
    email:        contact.email || '',
    phone:        contact.phone || '',
    contactType:  contact.contact_type || 'general_staff',
    portalRole:   contact.portal_role || 'member_contact',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.firstName || !form.lastName) return toast.error('First and last name required.');
    setSaving(true);
    try {
      await api.patch(`/contacts/${contact.id}`, form);
      toast.success('Contact updated.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Contact</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Salutation</label>
              <input className="form-input" value={form.salutation} onChange={e => set('salutation', e.target.value)} placeholder="Mr., Ms., Dr., etc." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Type</label>
              <select className="form-select" value={form.contactType} onChange={e => set('contactType', e.target.value)}>
                <option value="primary_contact">Primary Contact</option>
                <option value="decision_maker">Decision Maker</option>
                <option value="billing_contact">Billing Contact</option>
                <option value="event_coordinator">Event Coordinator</option>
                <option value="general_staff">General Staff</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Portal Access</label>
            <select className="form-select" value={form.portalRole} onChange={e => set('portalRole', e.target.value)}>
              <option value="member_admin">Member Admin (full access)</option>
              <option value="member_contact">Member Contact (read only)</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function EditMemberModal({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    businessName:     member.business_name || '',
    email:            member.email || '',
    phone:            member.phone || '',
    website:          member.website || '',
    addressLine1:     member.address_line1 || '',
    addressLine2:     member.address_line2 || '',
    city:             member.city || '',
    state:            member.state || '',
    zip:              member.zip || '',
    membershipTypeId: member.membership_type_id || '',
    industryId:       member.industry_id || '',
    renewalMonth:     member.renewal_month || '',
    salespersonId:    member.salesperson_id || '',
    memberSince:      member.member_since ? new Date(member.member_since).toISOString().split('T')[0] : '',
    status:           member.status || 'active',
    notes:            member.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [refData, setRefData] = useState({ memberTypes: [], industries: [], salespersons: [] });

  useEffect(() => {
    Promise.all([
      api.get('/lookups/membership-types'),
      api.get('/lookups/industries'),
      api.get('/lookups/salespersons'),
    ]).then(([mt, ind, sp]) => {
      setRefData({
        memberTypes:  mt.data || [],
        industries:   ind.data?.flat || ind.data || [],
        salespersons: sp.data || [],
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.businessName.trim()) return toast.error('Business name is required.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        membershipTypeId: form.membershipTypeId || null,
        industryId:       form.industryId || null,
        salespersonId:    form.salespersonId || null,
        renewalMonth:     form.renewalMonth || null,
        memberSince:      form.memberSince || null,
      };
      await api.patch(`/businesses/${member.id}`, payload);
      toast.success('Business updated.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Business</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Business Name *</label>
            <input className="form-input" value={form.businessName} onChange={e => set('businessName', e.target.value)} autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Website</label>
            <input className="form-input" value={form.website} onChange={e => set('website', e.target.value)} placeholder="www.example.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Address Line 1</label>
            <input className="form-input" value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Address Line 2</label>
            <input className="form-input" value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={form.state} onChange={e => set('state', e.target.value)} maxLength={2} placeholder="NJ" />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input className="form-input" value={form.zip} onChange={e => set('zip', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Membership Type</label>
              <select className="form-select" value={form.membershipTypeId} onChange={e => set('membershipTypeId', e.target.value)}>
                <option value="">— None —</option>
                {refData.memberTypes.map(mt => <option key={mt.id} value={mt.id}>{mt.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select className="form-select" value={form.industryId} onChange={e => set('industryId', e.target.value)}>
                <option value="">— None —</option>
                {refData.industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Renewal Month</label>
              <select className="form-select" value={form.renewalMonth} onChange={e => set('renewalMonth', e.target.value)}>
                <option value="">— None —</option>
                <option value="January">January</option>
                <option value="July">July</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Salesperson</label>
              <select className="form-select" value={form.salespersonId} onChange={e => set('salespersonId', e.target.value)}>
                <option value="">— None —</option>
                {refData.salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.full_name || sp.initials}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Member Since</label>
              <input className="form-input" type="date" value={form.memberSince} onChange={e => set('memberSince', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="grace_period">Grace Period</option>
                <option value="lapsed">Lapsed</option>
                <option value="non_member">Non-Member</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, link }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--gray-500)', width: 130, flexShrink: 0, fontWeight: 500 }}>{label}</span>
      {link && typeof value === 'string' ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" style={{ color: 'var(--navy)' }}>{value}</a>
      ) : (
        <span style={{ color: 'var(--gray-900)' }}>{value}</span>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ─── AddInvoiceModal (Phase 5) ───────────────────────────────────────────────
function AddInvoiceModal({ businessId, primaryContact, onClose, onSaved }) {
  const [form, setForm] = useState({
    invoiceType: 'general',
    dueDate: '',
    notes: '',
    contactId: primaryContact?.id || '',
  });
  const [lineItems, setLineItems] = useState([{ name: '', description: '', unitPrice: '', quantity: 1 }]);
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonId, setSalespersonId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/lookups/salespersons').then(r => setSalespersons(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setLineItems(items => items.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const addRow = () => setLineItems(items => [...items, { name: '', description: '', unitPrice: '', quantity: 1 }]);
  const removeRow = (i) => setLineItems(items => items.length > 1 ? items.filter((_, j) => j !== i) : items);

  const subtotal = lineItems.reduce((s, it) => s + (parseFloat(it.unitPrice || 0) * parseFloat(it.quantity || 0)), 0);

  const handleSave = async () => {
    const valid = lineItems.filter(it => it.name?.trim() && parseFloat(it.unitPrice) > 0);
    if (valid.length === 0) {
      toast.error('Add at least one line item with a name and price.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/invoices', {
        businessId: parseInt(businessId, 10),
        contactId: form.contactId || null,
        invoiceType: form.invoiceType,
        dueDate: form.dueDate || null,
        salespersonId: salespersonId || null,
        notes: form.notes || null,
        lineItems: valid.map(it => ({
          name: it.name.trim(),
          description: it.description || null,
          unitPrice: parseFloat(it.unitPrice),
          quantity: parseFloat(it.quantity || 1),
        })),
      });
      toast.success('Invoice created.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invoice.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add Invoice</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.invoiceType} onChange={e => set('invoiceType', e.target.value)}>
                <option value="general">General</option>
                <option value="membership">Membership</option>
                <option value="event_ticket">Event Ticket</option>
                <option value="sponsorship">Sponsorship</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-input" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Salesperson</label>
              <select className="form-select" value={salespersonId} onChange={e => setSalespersonId(e.target.value)}>
                <option value="">— None —</option>
                {salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.full_name || sp.initials}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Line Items *</label>
            <table style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ color: 'var(--gray-500)', fontSize: '0.75rem', textAlign: 'left' }}>
                  <th style={{ paddingBottom: 4 }}>Name</th>
                  <th style={{ paddingBottom: 4, width: 100 }}>Unit Price</th>
                  <th style={{ paddingBottom: 4, width: 80 }}>Qty</th>
                  <th style={{ paddingBottom: 4, width: 90, textAlign: 'right' }}>Total</th>
                  <th style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((it, i) => {
                  const lineTotal = parseFloat(it.unitPrice || 0) * parseFloat(it.quantity || 0);
                  return (
                    <tr key={i}>
                      <td style={{ paddingRight: 6 }}>
                        <input className="form-input" placeholder="e.g. Annual dues" value={it.name} onChange={e => setItem(i, 'name', e.target.value)} />
                      </td>
                      <td style={{ paddingRight: 6 }}>
                        <input type="number" step="0.01" className="form-input" value={it.unitPrice} onChange={e => setItem(i, 'unitPrice', e.target.value)} />
                      </td>
                      <td style={{ paddingRight: 6 }}>
                        <input type="number" step="0.01" className="form-input" value={it.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
                      </td>
                      <td style={{ textAlign: 'right', paddingRight: 6 }}>${lineTotal.toFixed(2)}</td>
                      <td>
                        <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeRow(i)} style={{ color: 'var(--danger)' }}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addRow} style={{ marginTop: 6 }}>+ Add line item</button>
            <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 600 }}>
              Subtotal: ${subtotal.toFixed(2)}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Invoice (draft)'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AddSponsorshipModal (Phase 5) ───────────────────────────────────────────
// Lets staff create a sponsorship and (optionally) an auto-linked invoice
function AddSponsorshipModal({ businessId, businessName, primaryContact, onClose, onSaved }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [tiers, setTiers] = useState([]);
  const [tierId, setTierId] = useState('');
  const [form, setForm] = useState({
    sponsorName:  businessName || '',
    sponsorEmail: primaryContact?.email || '',
    amount:       '',
    notes:        '',
    createInvoice: true,
    invoiceDueDate: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/events?limit=200').then(r => setEvents(r.data.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) { setTiers([]); setTierId(''); return; }
    api.get(`/events/${eventId}/sponsorship-tiers`).then(r => setTiers(r.data || [])).catch(() => setTiers([]));
  }, [eventId]);

  const onTierChange = (id) => {
    setTierId(id);
    const tier = tiers.find(t => String(t.id) === String(id));
    if (tier && tier.price) setForm(f => ({ ...f, amount: tier.price }));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!eventId) return toast.error('Pick an event.');
    if (!form.sponsorName.trim()) return toast.error('Sponsor name required.');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Amount required.');
    setSaving(true);
    try {
      const res = await api.post(`/events/${eventId}/sponsorships`, {
        sponsorshipTierId: tierId ? parseInt(tierId, 10) : null,
        businessId:        parseInt(businessId, 10),
        contactId:         primaryContact?.id || null,
        sponsorName:       form.sponsorName.trim(),
        sponsorEmail:      form.sponsorEmail || null,
        amount:            parseFloat(form.amount),
        notes:             form.notes || null,
        createInvoice:     !!form.createInvoice,
        invoiceDueDate:    form.invoiceDueDate || null,
      });
      const msg = res.data?.invoice
        ? `Sponsorship created. Linked invoice #${res.data.invoice.invoice_number} (draft).`
        : 'Sponsorship created.';
      toast.success(msg);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create sponsorship.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add Sponsorship</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event *</label>
            <select className="form-select" value={eventId} onChange={e => setEventId(e.target.value)}>
              <option value="">— select an event —</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name} {ev.event_date ? `(${new Date(ev.event_date).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
          </div>
          {eventId && tiers.length > 0 && (
            <div className="form-group">
              <label className="form-label">Sponsorship Tier</label>
              <select className="form-select" value={tierId} onChange={e => onTierChange(e.target.value)}>
                <option value="">— Custom amount (no tier) —</option>
                {tiers.map(t => <option key={t.id} value={t.id}>{t.name} — ${parseFloat(t.price).toLocaleString()}</option>)}
              </select>
            </div>
          )}
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Sponsor Name *</label>
              <input className="form-input" value={form.sponsorName} onChange={e => set('sponsorName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount *</label>
              <input type="number" step="0.01" className="form-input" value={form.amount} onChange={e => set('amount', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Sponsor Email</label>
            <input type="email" className="form-input" value={form.sponsorEmail} onChange={e => set('sponsorEmail', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
              <input type="checkbox" checked={form.createInvoice} onChange={e => set('createInvoice', e.target.checked)} />
              <span style={{ fontWeight: 500 }}>Also create a linked invoice (draft)</span>
            </label>
            {form.createInvoice && (
              <div className="form-group" style={{ marginLeft: 24 }}>
                <label className="form-label">Invoice Due Date</label>
                <input type="date" className="form-input" value={form.invoiceDueDate}
                       onChange={e => set('invoiceDueDate', e.target.value)} style={{ maxWidth: 200 }} />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Create Sponsorship'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AddTicketFromMemberModal (Phase 5) ──────────────────────────────────────
function AddTicketFromMemberModal({ businessId, businessName, primaryContact, onClose, onSaved }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [eventDetail, setEventDetail] = useState(null);
  const [form, setForm] = useState({
    ticketTypeId:      '',
    buyerFirstName:    primaryContact?.first_name || '',
    buyerLastName:     primaryContact?.last_name || '',
    buyerEmail:        primaryContact?.email || '',
    buyerBusinessName: businessName || '',
    paymentStatus:     'paid',
    isComp:            false,
    promoCode:         '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/events?limit=200').then(r => setEvents(r.data.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) { setEventDetail(null); return; }
    api.get(`/events/${eventId}`)
      .then(r => {
        setEventDetail(r.data);
        if (r.data.ticketTypes?.length) {
          setForm(f => ({ ...f, ticketTypeId: r.data.ticketTypes[0].id }));
        }
      })
      .catch(() => setEventDetail(null));
  }, [eventId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!eventId) return toast.error('Pick an event.');
    if (!form.ticketTypeId) return toast.error('Pick a ticket type.');
    if (!form.buyerFirstName?.trim() || !form.buyerLastName?.trim() || !form.buyerEmail?.trim()) {
      return toast.error('Buyer first name, last name, and email are required.');
    }
    setSaving(true);
    try {
      await postTicket(eventId, {
        ticketTypeId:      parseInt(form.ticketTypeId, 10),
        buyerFirstName:    form.buyerFirstName.trim(),
        buyerLastName:     form.buyerLastName.trim(),
        buyerEmail:        form.buyerEmail.trim(),
        buyerBusinessName: form.buyerBusinessName || null,
        contactId:         primaryContact?.id || null,
        businessId:        parseInt(businessId, 10),
        promoCode:         form.promoCode || null,
        paymentStatus:     form.isComp ? 'comp' : form.paymentStatus,
        isComp:            form.isComp,
      });
      toast.success('Ticket created.');
      onSaved();
    } catch (err) {
      if (err instanceof SkippedDuplicate) return;
      toast.error(err.response?.data?.error || 'Failed to create ticket.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add Ticket</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event *</label>
            <select className="form-select" value={eventId} onChange={e => setEventId(e.target.value)}>
              <option value="">— select an event —</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name} {ev.event_date ? `(${new Date(ev.event_date).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
          </div>
          {eventDetail?.ticketTypes?.length > 0 && (
            <div className="form-group">
              <label className="form-label">Ticket Type *</label>
              <select className="form-select" value={form.ticketTypeId} onChange={e => set('ticketTypeId', e.target.value)}>
                {eventDetail.ticketTypes.map(tt => (
                  <option key={tt.id} value={tt.id}>
                    {tt.name} — Member ${tt.member_price} / Non-member ${tt.non_member_price}
                  </option>
                ))}
              </select>
            </div>
          )}
          <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
            <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>Buyer (pre-filled from primary contact)</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label className="form-label">First Name *</label>
                <input className="form-input" value={form.buyerFirstName} onChange={e => set('buyerFirstName', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Last Name *</label>
                <input className="form-input" value={form.buyerLastName} onChange={e => set('buyerLastName', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.buyerEmail} onChange={e => set('buyerEmail', e.target.value)} />
            </div>
          </fieldset>
          <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
            <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>Payment</legend>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={!form.isComp && form.paymentStatus === 'paid'} onChange={() => set('paymentStatus', 'paid')} disabled={form.isComp} />
                Paid
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={!form.isComp && form.paymentStatus === 'pending'} onChange={() => set('paymentStatus', 'pending')} disabled={form.isComp} />
                Pending
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isComp} onChange={e => set('isComp', e.target.checked)} />
                Comp (free)
              </label>
            </div>
            <div>
              <label className="form-label">Promo Code (optional)</label>
              <input className="form-input" value={form.promoCode}
                     onChange={e => set('promoCode', e.target.value.toUpperCase())}
                     style={{ textTransform: 'uppercase', maxWidth: 240 }} disabled={form.isComp} />
            </div>
          </fieldset>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
